/**
 * steps/AssetsStep.tsx
 * Asset selection step for the onboarding wizard
 * 
 * Handles asset class selection with HeroUI CheckboxGroup.
 * Provides clean card-based UI following established wizard step patterns.
 * Integrates with draft persistence system and follows wizard step pattern.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { TrendingUp, Info } from 'lucide-react';

// 2. Internal absolute imports
import { CheckboxGroup, Checkbox, Card, CardHeader, CardBody, CardFooter, Alert, Accordion, AccordionItem } from '@enfomentis/ui';
import type { AssetClassType } from '@/types/shared/AssetClass';
import { PERSONAL_ASSET_CLASSES } from '@/constants/assetClassData';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Asset selection step component
 */
export const AssetsStep: React.FC = () => {
  const { draft, updateAccount } = useOnboardingData();
  
  // Local form state
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);

  // Initialize from draft
  useEffect(() => {
    if (draft?.account?.selectedAssetClasses) {
      setSelectedAssets(draft.account.selectedAssetClasses);
    }
  }, [draft]);

  /**
   * Handle asset selection changes
   */
  const handleAssetChange = (values: string[]): void => {
    setSelectedAssets(values);
    
    // Update draft immediately
    updateAccount({
      selectedAssetClasses: values as AssetClassType[]
    });
  };

  /**
   * Check if form is valid for navigation
   */
  const isFormValid = (): boolean => {
    return selectedAssets.length > 0;
  };

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          <span className="text-large font-semibold">Assets Traded</span>
        </div>
      </CardHeader>
      <CardBody className="space-y-6">
        <div className="max-w-2xl mx-auto px-2">
          {/* Asset Selection */}
          <div className="space-y-6">
            <CheckboxGroup
              description="What do you trade? Select all that apply. This helps us show relevant brokers and features."
              value={selectedAssets}
              onValueChange={handleAssetChange}
              orientation="vertical"
              size="lg"
              classNames={{
                wrapper: "space-y-3"
              }}
            >
              {PERSONAL_ASSET_CLASSES.map((asset) => (
                <Checkbox
                  key={asset.id}
                  value={asset.id}
                  size="lg"
                  classNames={{
                    base: "max-w-none w-full m-0 p-3 border border-border-light rounded-lg hover:border-border-medium hover:shadow-sm transition-all duration-200 data-[selected=true]:border-primary data-[selected=true]:bg-default-50",
                    wrapper: "mr-3"
                  }}
                >
                  <div className="flex items-center gap-3 w-full">
                    <span className="font-medium text-text-primary">
                      {asset.name}
                    </span>
                  </div>
                </Checkbox>
              ))}
            </CheckboxGroup>

            {/* Validation Status */}
            {isFormValid() && (
              <div className="flex items-center gap-2 text-success text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                Assets selected! Ready to continue.
              </div>
            )}

            {/* Validation Error */}
            {selectedAssets.length === 0 && (
              <Alert
                color="warning"
                variant="bordered"
                description="Please select at least one asset class to continue."
              />
            )}
          </div>

          
        </div>
      </CardBody>

      <CardFooter className="flex-shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            textValue="Why do we need this?"
            key="why-needed"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why do we need this?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• Your asset selection helps us show relevant brokers</li>
              <li>• Different assets have different trading requirements</li>
              <li>• Enables personalized guidance throughout setup</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>

      
    </Card>
  );
};